import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import User from "@insite/mobius/Icons/User";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

const mapStateToProps = (state: ApplicationState) => ({
    isSigningIn: state.context.isSigningIn,
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url.toLowerCase(),
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    showInventory: state.context.showInventory,
    showPricing: state.context.showPricing,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
    onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
    currentLocation: getLocation(state),
    firstName: state.context.session.firstName,
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    isSalesPerson: state.context.session?.isSalesPerson,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    letterStyle?: InjectableCss;
    userLink?: InjectableCss;
    myAccountMenu?: GridContainerProps;
    myAccountMenuList?: InjectableCss;
    myAccountMenuLink?: InjectableCss;
    openMyAccountMenu?: InjectableCss;
    iconStyle?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: flex;
            button {
                margin-left: 0;
                font-size: 13px;
            }
            nav {
                &:hover {
                    span,
                    p {
                        color: hsl(0, 0%, 70%);
                    }
                }
            }
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 0;
            flex-direction: column;
        `,
    },
    titleIcon: {
        size: 22,
        css: css`
            color: ${getColor("text.main")};
            margin-right: 5px;
        `,
    },
    letterStyle: {
        css: css`
            width: 40px;
            height: 40px;
            border-radius: 50%;
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #2d3435;
            div {
                width: 35px;
                height: 35px;
                padding-right: 4px;
                border: 1px solid #fff;
                border-radius: 50%;
                display: -webkit-box;
                display: -webkit-flex;
                display: -ms-flexbox;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-size: 30px;
                font-family: Courier;
                font-style: italic;
                font-weight: 500;
                text-transform: uppercase;
            }
        `,
    },

    userLink: {
        css: css`
            span {
                text-decoration: none !important;
            }
        `,
    },

    titleTypography: {
        css: css`
            font-size: 12px;
        `,
        ellipsis: true,
        transform: "capitalize",
        color: "text.main",
        // variant: "headerSecondary",
    },
    iconStyle: {
        css: css`
            background: none !important;
            color: ${getColor("text.main")};
            width: 14px !important;
            height: 14px !important;
            vertical-align: middle;
            padding: 0 !important;
            svg {
                color: ${getColor("text.main")} !important;
                width: 100%;
                height: 100%;
            }
        `,
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: capitalize;
            font-weight: 500;
            text-align: left;
            color: #193066;
            font-size: 13px;
            &:hover {
                text-decoration: underline;
                color: #112247;
            }
        `,
    },
    myAccountMenu: {
        css: css`
            display: none;
        `,
    },
    myAccountMenuList: {
        css: css`
            width: 100%;
            padding: 5px 0;
            max-width: 100%;
        `,
    },
    myAccountMenuLink: {
        css: css`
            display: block;
            margin: 0;
            padding: 0;
            text-align: left;
        `,
    },
    openMyAccountMenu: {
        css: css`
            display: block;
            background: #fff;
            box-shadow:
                0px 2px 4px -1px rgba(0, 0, 0, 0.2),
                0px 4px 5px 0px rgba(0, 0, 0, 0.14),
                0px 1px 10px 0px rgba(0, 0, 0, 0.12);
            padding: 5px 0 10px 0;
            position: absolute;
            width: 190px;
            z-index: 9;
            left: -18%;
            top: 100%;
            ul {
                display: block !important;
                width: 100% !important;
                margin: 0;
                padding: 0;
                li {
                    a {
                        span {
                            font-size: 13px;
                        }
                    }
                    &.signOutClickableWrapper {
                        padding: 5px 0;
                    }
                }
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;
const styles = headerSignInStyles;
let icon: React.ComponentType<IconPresentationProps>;
const HeaderSignIn = ({
    userName,
    isSigningIn,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    displayChangeCustomerLink,
    theme,
    firstName,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
    isSalesPerson,
}: Props) => {
    const [hovered, setHovered] = useState(false);
    const hoverIn = () => setHovered(true);
    const hoverOut = () => setHovered(false);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = User;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };
    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");
    }

    showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
    showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/myaccount/SignIn");
                return;
            }
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = firstName || userName;
    } else if (isSigningIn) {
        signInStatusText = translate("Signing in...");
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {includeAccountMenu && userName && !currentUserIsGuest && (
                <StyledWrapper onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                    <Link href="/myaccount" {...styles.userLink}>
                        <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
                            {/* {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />} */}
                            {showIcon && (
                                <StyledWrapper {...styles.letterStyle} data-test-selector="header_signInLetter">
                                    <StyledWrapper>{signInStatusText[0]}</StyledWrapper>
                                </StyledWrapper>
                            )}
                            {showLabel && (
                                <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                    My Account <Icon {...styles.iconStyle} size={14} src={ChevronDown} />
                                </Typography>
                            )}
                        </Clickable>
                    </Link>
                    <GridContainer
                        {...(hovered ? { ...styles.openMyAccountMenu } : { ...styles.myAccountMenu })}
                        offsetProps={{ ...styles.myAccountMenu?.offsetProps, as: "ul" }}
                        onMouseEnter={hoverIn}
                        onMouseLeave={hoverOut}
                    >
                        {menuItems
                            ?.filter(child => !child.excludeFromNavigation)
                            .map((child, index) => (
                                <GridItem {...styles.myAccountMenuList} key={index} as="li">
                                    <Link {...styles.myAccountMenuLink} href={child.url} onClick={hoverOut}>
                                        {child.title}
                                    </Link>
                                </GridItem>
                            ))}
                        {isSalesPerson && (
                            <GridItem {...styles.myAccountMenuList} as="li">
                                <Link
                                    {...styles.myAccountMenuLink}
                                    href={"/MyAccount/CreateNewCustomer"}
                                    onClick={hoverOut}
                                >
                                    {"Create New Customer"}
                                </Link>
                            </GridItem>
                        )}
                        <li className="signOutClickableWrapper">
                            <Clickable
                                {...styles.signOutClickable}
                                onClick={onSignOutHandler}
                                data-test-selector="headerSignOutLink"
                            >
                                {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                            </Clickable>
                        </li>
                    </GridContainer>
                </StyledWrapper>
            )}
            {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <Clickable
                    {...styles.titleClickable}
                    onClick={onSignInHandler}
                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                    data-test-selector="header_signIn"
                >
                    {showIcon && <Icon {...styles.titleIcon} src={icon} title={signInStatusText} />}
                    {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
                </Clickable>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
